import React, { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import AdminProductService from "../../../Modules/Services/admin.products.service.ts";
import AdminCategoryService from "../../../Modules/Services/admin.categories.service";
import API_BASE_URL from "../../../config";
import { AdminModal, AdminFlash } from "../../../Components/Admin/AdminOverlays";

const emptyVariant = () => ({
  variant_id: null,
  size: "",
  weight: "",
  price: "",
});

const ManageProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState("");
  const [composition, setComposition] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [isStopList, setIsStopList] = useState(false);
  const [variants, setVariants] = useState([emptyVariant()]);
  const [imageFile, setImageFile] = useState(null);
  const [flash, setFlash] = useState({ type: "", text: "" });
  const [togglingIds, setTogglingIds] = useState(() => new Set());
  const [togglingCategoryIds, setTogglingCategoryIds] = useState(() => new Set());

  const showFlash = (type, text) => {
    setFlash({ type, text });
    if (text) window.setTimeout(() => setFlash({ type: "", text: "" }), 4000);
  };

  const mapCategoriesToProducts = (categoriesWithProducts) =>
    categoriesWithProducts.flatMap((category) =>
      category.products.map((product) => ({
        ...product,
        category: {
          category_id: category.category_id,
          category_name: category.category_name,
        },
      }))
    );

  const fetchProducts = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const categoriesWithProducts = await AdminProductService.getAllProducts();
      setProducts(mapCategoriesToProducts(categoriesWithProducts));
    } catch {
      showFlash("error", "Ошибка загрузки товаров.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const productsByCategory = useMemo(() => {
    const map = new Map();
    for (const product of products) {
      const categoryId = product.category?.category_id;
      if (categoryId == null) continue;
      if (!map.has(categoryId)) {
        map.set(categoryId, {
          category_id: categoryId,
          category_name: product.category?.category_name || "—",
          products: [],
        });
      }
      map.get(categoryId).products.push(product);
    }
    return Array.from(map.values()).sort((a, b) =>
      (a.category_name || "").localeCompare(b.category_name || "", "ru")
    );
  }, [products]);

  const fetchCategories = async () => {
    try {
      const cats = await AdminCategoryService.getAllCategories();
      setCategories(cats);
    } catch {
      showFlash("error", "Не удалось загрузить категории.");
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const resetForm = () => {
    setName("");
    setComposition("");
    setCategoryId("");
    setIsStopList(false);
    setVariants([emptyVariant()]);
    setImageFile(null);
  };

  useEffect(() => {
    if (!modalOpen) return;
    if (editingProduct) {
      setName(editingProduct.product_name || "");
      setComposition(editingProduct.composition || "");
      setCategoryId(String(editingProduct.category.category_id));
      setIsStopList(Boolean(editingProduct.is_stop_list));
      const v = editingProduct.variants?.length
        ? editingProduct.variants.map((x) => ({
            variant_id: x.variant_id ?? null,
            size: x.size ?? "",
            weight: x.weight ?? "",
            price: x.price != null ? String(x.price) : "",
          }))
        : [emptyVariant()];
      setVariants(v);
      setImageFile(null);
    } else {
      resetForm();
    }
  }, [editingProduct, modalOpen]);

  const updateVariant = (index, field, value) => {
    setVariants((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addVariant = () => setVariants((v) => [...v, emptyVariant()]);
  const removeVariant = (index) =>
    setVariants((v) => v.filter((_, i) => i !== index));

  const buildVariantsPayload = () => {
    const parsed = variants.map((row, i) => ({
      variant_id: row.variant_id,
      size: row.size.trim(),
      weight: row.weight.trim(),
      price: row.price,
    }));
    for (const row of parsed) {
      if (!row.size || !row.weight || row.price === "") {
        throw new Error("Заполните размер, вес и цену для каждого варианта.");
      }
      const priceNum = Number(row.price);
      if (!Number.isFinite(priceNum)) throw new Error("Некорректная цена.");
      row.price = priceNum;
    }
    return parsed;
  };

  const handleSave = async () => {
    try {
      const trimmedName = name.trim();
      if (!trimmedName) {
        showFlash("error", "Введите название.");
        return;
      }
      if (!composition.trim()) {
        showFlash("error", "Введите состав.");
        return;
      }
      if (!categoryId) {
        showFlash("error", "Выберите категорию.");
        return;
      }
      let varsJson;
      try {
        varsJson = JSON.stringify(buildVariantsPayload());
      } catch (e) {
        showFlash("error", e.message || "Проверьте варианты.");
        return;
      }

      const formData = new FormData();
      formData.append("name", trimmedName);
      formData.append("composition", composition.trim());
      formData.append("category_id", categoryId);
      formData.append("is_stop_list", isStopList ? "1" : "0");
      formData.append("variants", varsJson);
      if (imageFile) formData.append("image", imageFile);

      if (editingProduct) {
        await AdminProductService.updateProduct(
          editingProduct.product_id,
          formData
        );
        showFlash("success", "Товар обновлён.");
      } else {
        await AdminProductService.createProduct(formData);
        showFlash("success", "Товар создан.");
      }
      setModalOpen(false);
      setEditingProduct(null);
      fetchProducts({ silent: true });
    } catch {
      showFlash("error", "Не удалось сохранить.");
    }
  };

  const setProductStopList = (productId, isStopList) => {
    setProducts((prev) =>
      prev.map((item) =>
        item.product_id === productId
          ? { ...item, is_stop_list: isStopList }
          : item
      )
    );
  };

  const setCategoryStopListLocal = (categoryId, isStopList) => {
    setProducts((prev) =>
      prev.map((item) =>
        item.category?.category_id === categoryId
          ? { ...item, is_stop_list: isStopList }
          : item
      )
    );
  };

  const handleToggleStopList = async (product) => {
    const productId = product.product_id;
    const nextStop = !product.is_stop_list;

    setProductStopList(productId, nextStop);
    setTogglingIds((prev) => new Set(prev).add(productId));

    try {
      await AdminProductService.updateProduct(productId, {
        name: product.product_name,
        category_id: product.category.category_id,
        composition: product.composition,
        image_url: product.image_url,
        is_stop_list: nextStop,
      });
    } catch {
      setProductStopList(productId, !nextStop);
      showFlash("error", "Не удалось обновить стоп-лист.");
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  const handleToggleCategoryStopList = async (group) => {
    const { category_id: categoryId, products: categoryProducts } = group;
    const allStopped = categoryProducts.every((p) => p.is_stop_list);
    const nextStop = !allStopped;
    const previous = categoryProducts.map((p) => ({
      product_id: p.product_id,
      is_stop_list: p.is_stop_list,
    }));

    setCategoryStopListLocal(categoryId, nextStop);
    setTogglingCategoryIds((prev) => new Set(prev).add(categoryId));

    try {
      await AdminCategoryService.setCategoryStopList(categoryId, nextStop);
    } catch {
      for (const item of previous) {
        setProductStopList(item.product_id, item.is_stop_list);
      }
      showFlash("error", "Не удалось обновить стоп-лист категории.");
    } finally {
      setTogglingCategoryIds((prev) => {
        const next = new Set(prev);
        next.delete(categoryId);
        return next;
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить товар?")) return;
    try {
      await AdminProductService.deleteProduct(id);
      setProducts((prev) => prev.filter((p) => p.product_id !== id));
      showFlash("success", "Товар удалён.");
    } catch {
      showFlash("error", "Не удалось удалить.");
    }
  };

  return (
    <div>
      <h1 className="admin-page-title">Товары</h1>

      {flash.text ? (
        <AdminFlash
          type={flash.type}
          message={flash.text}
          onClose={() => setFlash({ type: "", text: "" })}
        />
      ) : null}

      <div className="admin-toolbar">
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => {
            setEditingProduct(null);
            setModalOpen(true);
          }}
        >
          Создать товар
        </button>
      </div>

      <div className="admin-table-wrap">
        {loading ? (
          <div className="admin-spinner">Загрузка…</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Название</th>
                <th>Категория</th>
                <th>Стоп-лист</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {productsByCategory.map((group) => {
                const allStopped = group.products.every((p) => p.is_stop_list);
                const categoryBusy = togglingCategoryIds.has(group.category_id);

                return (
                  <React.Fragment key={group.category_id}>
                    <tr className="admin-table-category-row">
                      <td colSpan={5}>
                        <div className="admin-table-category-head">
                          <strong>{group.category_name}</strong>
                          <button
                            type="button"
                            className="btn btn--outline btn--small"
                            disabled={categoryBusy || !group.products.length}
                            onClick={() => handleToggleCategoryStopList(group)}
                          >
                            {categoryBusy
                              ? "Обновление…"
                              : allStopped
                                ? "Снять стоп с категории"
                                : "Всю категорию в стоп"}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {group.products.map((record) => {
                      const rowBusy = togglingIds.has(record.product_id);

                      return (
                        <tr key={record.product_id}>
                          <td>{record.product_id}</td>
                          <td>{record.product_name}</td>
                          <td>{record.category?.category_name}</td>
                          <td>{record.is_stop_list ? "Да" : "Нет"}</td>
                          <td>
                            <div className="admin-table-actions">
                              <button
                                type="button"
                                className="btn btn--primary btn--small"
                                onClick={() => {
                                  setEditingProduct(record);
                                  setModalOpen(true);
                                }}
                              >
                                Изменить
                              </button>
                              <button
                                type="button"
                                className="btn btn--ghost btn--small"
                                disabled={rowBusy}
                                onClick={() => handleToggleStopList(record)}
                              >
                                {rowBusy
                                  ? "…"
                                  : record.is_stop_list
                                    ? "Снять стоп"
                                    : "В стоп-лист"}
                              </button>
                              <button
                                type="button"
                                className="btn btn--danger btn--small"
                                onClick={() => handleDelete(record.product_id)}
                              >
                                Удалить
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <AdminModal
        open={modalOpen}
        title={editingProduct ? "Редактировать товар" : "Новый товар"}
        wide
        onClose={() => {
          setModalOpen(false);
          setEditingProduct(null);
        }}
        footer={
          <>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setModalOpen(false);
                setEditingProduct(null);
              }}
            >
              Отмена
            </button>
            <button type="button" className="btn btn--primary" onClick={handleSave}>
              Сохранить
            </button>
          </>
        }
      >
        <div className="admin-field">
          <label className="admin-field__label">Название</label>
          <input
            className="admin-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="admin-field">
          <label className="admin-field__label">Состав</label>
          <textarea
            className="admin-textarea"
            rows={3}
            value={composition}
            onChange={(e) => setComposition(e.target.value)}
          />
        </div>
        <div className="admin-field">
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={isStopList}
              onChange={(e) => setIsStopList(e.target.checked)}
            />
            В стоп-листе
          </label>
        </div>
        <div className="admin-field">
          <label className="admin-field__label" htmlFor="prod-cat">
            Категория
          </label>
          <select
            id="prod-cat"
            className="admin-select"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">— выберите —</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
        <div className="admin-field">
          <span className="admin-field__label">Изображение</span>
          <input
            type="file"
            accept="image/*"
            className="admin-file"
            onChange={(e) => setImageFile(e.target.files?.[0] || null)}
          />
          {editingProduct?.image_url && !imageFile ? (
            <div style={{ marginTop: "0.5rem" }}>
              <img
                src={`${API_BASE_URL}${editingProduct.image_url}`}
                alt=""
                style={{
                  maxWidth: 120,
                  borderRadius: 8,
                  border: "1px solid var(--border)",
                }}
              />
            </div>
          ) : null}
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Варианты (размер / вес / цена)</span>
          {variants.map((row, idx) => (
            <div key={idx} className="admin-variant-row">
              <input
                className="admin-input"
                placeholder="Размер"
                value={row.size}
                onChange={(e) => updateVariant(idx, "size", e.target.value)}
              />
              <input
                className="admin-input"
                placeholder="Вес"
                value={row.weight}
                onChange={(e) => updateVariant(idx, "weight", e.target.value)}
              />
              <input
                className="admin-input"
                placeholder="Цена"
                value={row.price}
                onChange={(e) => updateVariant(idx, "price", e.target.value)}
              />
              <button
                type="button"
                className="btn btn--ghost btn--small"
                disabled={variants.length <= 1}
                onClick={() => removeVariant(idx)}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn--outline btn--small"
            style={{ marginTop: "0.35rem" }}
            onClick={addVariant}
          >
            <Plus size={14} style={{ marginRight: 4 }} />
            Вариант
          </button>
        </div>
      </AdminModal>
    </div>
  );
};

export default ManageProducts;
